import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import type {
  AdminHealth,
  HealthApp,
  HealthPoint,
  HealthRange,
  HealthSeries,
  HealthService,
} from '@traceio-org/platform-contracts';
import { HEALTH_RANGES } from '@traceio-org/platform-contracts';
import {
  ActivityFeed,
  AnalyticsBreakdown,
  AnalyticsCard,
  AnalyticsGrid,
  ComparisonChart,
  EmptyPageState,
  PageHeading,
  PageSection,
  RecordStatus,
} from '@traceio-org/ui';
import { AdminPage } from '../components/AdminPage';
import { AdminProblem } from '../components/AdminProblem';
import { LoadingBlock, useNow } from '../components/feedback';
import { ScrollTable } from '../components/records';
import { useAdmin } from '../context';
import { formatDateTime, plural } from '../format';
import {
  APP_STATUS,
  DEPLOY_OUTCOMES,
  ENVIRONMENT_NAMES,
  RANGE_NAMES,
  appsNeedingAttention,
  chartValues,
  deploysToday,
  describeFailure,
  formatAge,
  formatLatency,
  formatMs,
  formatPercent,
  formatRate,
  hasValues,
  rangeParam,
  seriesSummary,
  sortApps,
  stepLabel,
} from '../health';
import { KeyGlyph } from '../keyProviders';
import { keysAttentionWords, keysNeedingAttention, useKeysSummary } from '../keysSummary';
import { HealthGlyph } from '../navigation';
import { useHealthRead } from '../useHealthRead';
import { MenuSelect, PeriodMenu } from '../components/MenuSelect';
import { UptimeGrid } from '../components/UptimeGrid';
import { formatUptime, overallPercent, type UptimePeriod } from '../uptime';
import type { AdminFailure } from '../api';

/** A value DigitalOcean did not return: said in words, never shown as zero. */
function Value({ text }: { text: string | null }) {
  return text === null ? (
    <span className="admin-muted" title="DigitalOcean returned no value for this">
      No data
    </span>
  ) : (
    <>{text}</>
  );
}

/**
 * CPU or memory of one app: the period's average, or, for an app DigitalOcean's Insights has no series for, the value
 * App Platform's component health reports now, marked "now" so it never passes for an average.
 */
function Resource({ value, source }: { value: number | null; source: 'period_average' | 'now' | null }) {
  const text = formatPercent(value);
  if (text === null || source !== 'now') return <Value text={text} />;
  return (
    <span title="DigitalOcean's Insights has no series for this app; this is App Platform's component health now">
      {text} <span className="admin-muted">now</span>
    </span>
  );
}

/** "Checked 12 s ago" with a dot: green when every read answered, amber when some failed. */
function CheckedAt({
  health,
  now,
  refreshFailure,
}: {
  health: AdminHealth;
  now: number;
  refreshFailure: AdminFailure | null;
}) {
  const partial = health.failures.length > 0 || refreshFailure !== null;
  return (
    <p className="admin-health-checked" data-state={partial ? 'partial' : 'ok'}>
      <span className="admin-health-checked__dot" aria-hidden="true" />
      <span>
        Checked{' '}
        <time dateTime={health.checkedAt} title={formatDateTime(health.checkedAt)}>
          {formatAge(health.checkedAt, now)}
        </time>
        {refreshFailure ? (
          <>, the last refresh failed</>
        ) : health.failures.length > 0 ? (
          <span className="admin-sr-only">, some reads failed</span>
        ) : null}
      </span>
    </p>
  );
}

/**
 * The metrics strip (Trace UI's trace-page-metrics-strip), values only: apps healthy now, the uptime grid's period
 * figure, the last five minutes' requests and p95 (summary.recent), and today's deploys. The p95 reads "Not enough
 * traffic" below about a request a minute, "No data" without a measurement; uptime reads "No data yet" until the
 * health-sample job has recorded a sample.
 */
function HealthMetrics({ health, uptimePeriod }: { health: AdminHealth; uptimePeriod: UptimePeriod }) {
  const { summary } = health;
  const { recent } = summary;
  const uptime = overallPercent(health.uptimeHistory, uptimePeriod);
  // Today is the UTC day of the answer, so the figure matches the deploys it was counted from.
  const deploys = deploysToday(health.apps, Date.parse(health.checkedAt));
  const metrics: { label: string; value: string | null; note?: string }[] = [
    {
      label: 'Apps healthy',
      value: `${summary.appsHealthy} of ${summary.appsTotal}`,
      ...(summary.appsNeedingAttention > 0
        ? { note: `${plural(summary.appsNeedingAttention, 'needs', 'need')} attention` }
        : {}),
    },
    {
      label: 'Uptime',
      value: uptime === null ? 'No data yet' : formatUptime(uptime),
      note: `All apps, last ${uptimePeriod} days`,
    },
    {
      label: 'Requests now',
      value: formatRate(recent.requestsPerSecond),
      note: `All apps, last ${recent.minutes} min`,
    },
    {
      label: 'p95 peak',
      value: formatLatency({
        latencyP95Ms: recent.latencyP95Ms,
        latencyP95AtTopBucket: recent.latencyP95AtTopBucket,
        latencyP95Reason: recent.reason,
      }),
      note: `The slowest app, last ${recent.minutes} min`,
    },
    {
      label: 'Deploys today',
      value: String(deploys.total),
      ...(deploys.failed > 0 ? { note: `${plural(deploys.failed, 'failed', 'failed')}` } : {}),
    },
  ];
  return (
    <section className="trace-page-metrics trace-page-metrics-strip" aria-label="Key metrics">
      {/* The strip is one row high (Trace UI), so each note is the label's hover text and is read out after the
          value. */}
      {metrics.map((metric) => (
        <div key={metric.label} className="trace-page-metric admin-health-metric" title={metric.note}>
          <span className="trace-page-metric-label">{metric.label}</span>
          <span className="trace-page-metric-value">
            <Value text={metric.value} />
            {metric.note ? <span className="admin-sr-only">, {metric.note}</span> : null}
          </span>
        </div>
      ))}
    </section>
  );
}

/** The charts' period, in the address (?range=7d); everything else on the page has its own window. */
function PeriodChoice({ range, onRange }: { range: HealthRange; onRange: (range: HealthRange) => void }) {
  return (
    <div className="admin-health-period">
      <PeriodMenu
        label="Charts period"
        value={range}
        options={HEALTH_RANGES.map((value) => ({ value, label: RANGE_NAMES[value] }))}
        onChange={onRange}
      />
    </div>
  );
}

/** Admin's amber banner, naming each app that needs someone and what is wrong in plain words. */
function AttentionBanner({ apps }: { apps: HealthApp[] }) {
  const headingId = useId();
  return (
    <section className="admin-access-banner admin-health-attention" aria-labelledby={headingId}>
      <span className="admin-access-banner__icon" aria-hidden="true">
        <HealthGlyph />
      </span>
      <div className="admin-access-banner__body">
        <h2 id={headingId}>
          {apps.length === 1
            ? `${apps[0]!.displayName} needs attention`
            : `${apps.length} apps need attention`}
        </h2>
        <ul className="admin-plain-list admin-plain-list--flush">
          {apps.map((app) => (
            <li key={app.id}>
              <strong>{app.displayName}</strong> ({APP_STATUS[app.status].label.toLowerCase()}):{' '}
              {app.reasons.join(' ')}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** The DigitalOcean reads that failed: what is missing, so a gap never reads as healthy. */
function PartialNotice({ health }: { health: AdminHealth }) {
  const headingId = useId();
  return (
    <section className="admin-health-partial" aria-labelledby={headingId}>
      <h2 id={headingId}>Some of DigitalOcean&apos;s answers are missing</h2>
      <p>What follows is shown without them; nothing missing is filled in.</p>
      <ul>
        {health.failures.map((failure, index) => (
          <li key={`${failure.read}-${failure.target ?? ''}-${index}`}>{describeFailure(failure)}</li>
        ))}
      </ul>
    </section>
  );
}

/** A chart's series in words: "CPU: average 2.4%, peak 2.6%" (or "no measurement"), one per series. */
function seriesWords(series: ChartSeries[], format: (value: number) => string): string[] {
  return series.map((s) => {
    const summary = seriesSummary(s.points);
    return `${s.label}: ${summary ? `average ${format(summary.average)}, peak ${format(summary.peak)}` : 'no measurement'}`;
  });
}

interface ChartSeries {
  id: string;
  label: string;
  points: HealthPoint[];
}

/** All apps, or one app by its DigitalOcean id: what a chart shows. */
type AppScope = 'all' | string;

/** One Health chart: its title, layout, value format, and its headline and series for all apps or for one app. */
interface ChartDefinition {
  id: string;
  title: string;
  span: 'wide' | 'small';
  format: (value: number) => string;
  headline: (health: AdminHealth, app: HealthApp | null) => string | null;
  series: (series: HealthSeries, app: HealthApp | null) => ChartSeries[];
}

const percent = (value: number) => formatPercent(value) ?? '';
const ms = (value: number) => formatMs(value) ?? '';
const rate = (value: number) => formatRate(value) ?? '';

/**
 * The charts. All apps by default (the environment's series and the period's summary), or one app (its own series
 * from the same Insights answers, and its own period figures with the same honesty: "No data", "Not enough traffic",
 * CPU marked "now" where DigitalOcean only reports it now).
 */
const CHARTS: readonly ChartDefinition[] = [
  {
    id: 'requests',
    title: 'Requests',
    span: 'wide',
    format: rate,
    headline: (health, app) =>
      formatRate(app ? app.metrics.requestsPerSecond : health.summary.requestsPerSecond),
    series: (series) => [{ id: 'requests', label: 'Requests per second', points: series.requestsPerSecond }],
  },
  {
    id: 'latency',
    title: 'p95 latency, peak',
    span: 'small',
    format: ms,
    headline: (health, app) => formatLatency(app ? app.metrics : health.summary),
    series: (series, app) => [
      { id: 'latency', label: app ? app.displayName : 'Slowest app', points: series.latencyP95Ms },
    ],
  },
  {
    id: 'resources',
    title: 'CPU and memory',
    span: 'wide',
    format: percent,
    headline: (health, app) => {
      if (app) {
        const cpu = formatPercent(app.metrics.cpuPercent);
        if (cpu === null) return null;
        return app.metrics.resourcesSource === 'now' ? `${cpu} CPU now` : `${cpu} CPU on average`;
      }
      const cpu = seriesSummary(health.series.cpuPercent);
      return cpu ? `${percent(cpu.average)} CPU on average` : null;
    },
    series: (series) => [
      { id: 'cpu', label: 'CPU', points: series.cpuPercent },
      { id: 'memory', label: 'Memory', points: series.memoryPercent },
    ],
  },
  {
    id: 'errors',
    title: '5xx share',
    span: 'small',
    format: (value) => formatPercent(value, 2) ?? '',
    headline: (health, app) =>
      formatPercent(app ? app.metrics.errorRatePercent : health.summary.errorRatePercent, 2),
    series: (series) => [{ id: 'errors', label: '5xx share', points: series.errorRatePercent }],
  },
];

/**
 * One chart card (Trace UI's AnalyticsCard): the headline up top, an app filter (Trace UI's menu, as the period's),
 * the chart (Trace UI's ComparisonChart, its palette in its fixed order) and its average and peak in words underneath.
 *
 * Accessibility without a table: the chart sits in a group whose name sums up the values ("Requests, all apps, last
 * 24 hours. Requests per second: average 0.82/s, peak 1.08/s"), and the same words stay visible under it. Not
 * role="img": Trace UI's chart is interactive (arrow keys inspect each step, its legend toggles series, a live line
 * reads the inspected step), and an image role would hide all of that from assistive technology.
 * Without ResizeObserver (which the chart measures with) only the words are shown.
 */
function ChartCard({ chart, health }: { chart: ChartDefinition; health: AdminHealth }) {
  const [scope, setScope] = useState<AppScope>('all');
  const apps = useMemo(
    () => [...health.apps].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [health.apps],
  );
  // An app that left the environment (a later refresh) falls back to all apps.
  const app = scope === 'all' ? null : (apps.find((candidate) => candidate.id === scope) ?? null);
  const series = chart.series(app ? app.series : health.series, app);
  const value = chart.headline(health, app);
  const first = series[0]?.points ?? [];
  const labels = first.map((point) => stepLabel(point.at, health.range));
  const measured = series.some((s) => hasValues(s.points));
  const chartable = measured && typeof ResizeObserver !== 'undefined';
  const who = app ? app.displayName : 'all apps';
  const words = seriesWords(series, chart.format);
  return (
    <AnalyticsCard
      title={chart.title}
      value={value ?? 'No data'}
      action={
        apps.length > 1 ? (
          <MenuSelect
            variant="compact"
            label={`${chart.title} app`}
            value={app ? app.id : 'all'}
            options={[
              { value: 'all', label: 'All apps' },
              ...apps.map((candidate) => ({ value: candidate.id, label: candidate.displayName })),
            ]}
            onChange={setScope}
          />
        ) : undefined
      }
    >
      {!measured ? (
        <p className="admin-muted">
          {/* An app Insights keeps no history for, whose CPU and memory App Platform reports now (the headline):
              there is a value, just no series to draw. */}
          {app && chart.id === 'resources' && app.metrics.resourcesSource === 'now'
            ? `Only the current value is available for ${app.displayName}.`
            : `DigitalOcean measured nothing for ${app ? app.displayName : 'this period'}.`}
        </p>
      ) : (
        <>
          {chartable ? (
            <div
              role="group"
              className="admin-health-chart"
              aria-label={`${chart.title}, ${who}, ${RANGE_NAMES[health.range].toLowerCase()}. ${words.join('. ')}`}
            >
              <ComparisonChart
                label={`${chart.title}, ${who}, ${RANGE_NAMES[health.range].toLowerCase()}`}
                labels={labels}
                series={series.map((s) => ({ id: s.id, label: s.label, values: chartValues(s.points) }))}
                formatValue={chart.format}
                presentation="area"
              />
            </div>
          ) : null}
          <ul className="admin-health-summary">
            {words.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      )}
    </AnalyticsCard>
  );
}

function HealthCharts({ health }: { health: AdminHealth }) {
  return (
    <AnalyticsGrid
      items={CHARTS.map((chart) => ({
        id: chart.id,
        span: chart.span,
        children: <ChartCard chart={chart} health={health} />,
      }))}
    />
  );
}

/** Activity's first page, and how many more each "Show more" adds: the list never grows without a click. */
export const ACTIVITY_FIRST = 6;
export const ACTIVITY_MORE = 10;

/** Deploys and incidents of the period, newest first: the newest six, then ten more per "Show more". */
function Activity({ health, now }: { health: AdminHealth; now: number }) {
  const [shown, setShown] = useState(ACTIVITY_FIRST);
  if (health.activity.length === 0) {
    return <p className="admin-muted">No deploys or incidents in this period.</p>;
  }
  const visible = health.activity.slice(0, shown);
  const rest = health.activity.length - visible.length;
  return (
    <>
      <ActivityFeed
        label="Deploys and incidents"
        items={visible.map((entry) => ({
          id: entry.id,
          title: entry.title,
          detail: entry.detail,
          time: formatAge(entry.at, now),
          kind: entry.tone,
        }))}
      />
      {rest > 0 ? (
        <button
          type="button"
          className="admin-action admin-health-more"
          onClick={() => setShown((count) => count + ACTIVITY_MORE)}
        >
          Show {Math.min(rest, ACTIVITY_MORE)} more
        </button>
      ) : null}
    </>
  );
}

/** The latest deploys as a strip, newest first; each mark carries its outcome and age in words. */
function DeployStrip({ app, now }: { app: HealthApp; now: number }) {
  if (app.recentDeploys.length === 0) return <span className="admin-muted">None</span>;
  const failed = app.recentDeploys.filter((d) => d.outcome === 'failed').length;
  return (
    <span className="admin-health-deploys">
      <ol className="admin-health-strip" aria-label={`Latest deploys of ${app.displayName}, newest first`}>
        {app.recentDeploys.map((deploy) => {
          const words = `${DEPLOY_OUTCOMES[deploy.outcome]} ${formatAge(deploy.createdAt, now)}${deploy.commit ? `, ${deploy.commit.slice(0, 7)}` : ''}`;
          return (
            <li key={deploy.id} data-outcome={deploy.outcome} title={words}>
              <span className="admin-sr-only">{words}</span>
            </li>
          );
        })}
      </ol>
      {failed > 0 ? <span className="admin-warning-text">{failed} failed</span> : null}
    </span>
  );
}

/** DigitalOcean's name (when the shown name differs) and the live host. */
function secondaryLabel(app: HealthApp): string {
  return [app.displayName === app.name ? null : app.name, app.host].filter(Boolean).join(' · ');
}

function AppsTable({ apps, now }: { apps: HealthApp[]; now: number }) {
  return (
    <ScrollTable caption="Apps" className="admin-table admin-table--health">
      <thead>
        <tr>
          <th scope="col">App</th>
          <th scope="col">Status</th>
          <th scope="col">Recent deploys</th>
          <th scope="col" className="admin-num">
            Uptime, 30 days
          </th>
          <th scope="col" className="admin-num">
            CPU, average
          </th>
          <th scope="col" className="admin-num">
            Memory, average
          </th>
          <th scope="col" className="admin-num">
            p95, peak
          </th>
          <th scope="col" className="admin-num">
            Requests, average
          </th>
          <th scope="col" className="admin-num">
            5xx
          </th>
          <th scope="col">Deployed</th>
        </tr>
      </thead>
      <tbody>
        {apps.map((app) => {
          const status = APP_STATUS[app.status];
          return (
            <tr key={app.id}>
              <th scope="row">
                <a href={app.consoleUrl} target="_blank" rel="noopener noreferrer">
                  {app.displayName}
                </a>
                {/* DigitalOcean's own name and the live host, so staff can find the app in the control panel. */}
                {secondaryLabel(app) ? (
                  <span className="admin-cell-secondary">{secondaryLabel(app)}</span>
                ) : null}
              </th>
              <td>
                <RecordStatus tone={status.tone}>{status.label}</RecordStatus>
                {app.inProgress ? <span className="admin-cell-secondary">Deploying now</span> : null}
              </td>
              <td>
                <DeployStrip app={app} now={now} />
              </td>
              <td className="admin-num">
                {app.uptime ? (
                  <span title={app.uptime.checkName}>
                    <Value text={formatPercent(app.uptime.percent30d, 2)} />
                  </span>
                ) : (
                  <span className="admin-muted">No check</span>
                )}
              </td>
              <td className="admin-num">
                <Resource value={app.metrics.cpuPercent} source={app.metrics.resourcesSource} />
              </td>
              <td className="admin-num">
                <Resource value={app.metrics.memoryPercent} source={app.metrics.resourcesSource} />
              </td>
              <td className="admin-num">
                <Value text={formatLatency(app.metrics)} />
              </td>
              <td className="admin-num">
                <Value text={formatRate(app.metrics.requestsPerSecond)} />
              </td>
              <td className="admin-num">
                <Value text={formatPercent(app.metrics.errorRatePercent, 2)} />
              </td>
              <td>
                {app.deployment ? (
                  <>
                    {app.deployment.commit ? <code>{app.deployment.commit.slice(0, 7)}</code> : null}{' '}
                    <time
                      dateTime={app.deployment.createdAt}
                      title={formatDateTime(app.deployment.createdAt)}
                    >
                      {formatAge(app.deployment.createdAt, now)}
                    </time>
                  </>
                ) : (
                  <span className="admin-muted">Nothing deployed</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </ScrollTable>
  );
}

function ServiceRows({ service }: { service: HealthService }) {
  const share = (value: number | null) => (value === null ? {} : { share: value });
  return (
    <div className="admin-health-service">
      <h3>
        <a href={service.consoleUrl} target="_blank" rel="noopener noreferrer">
          {service.name}
        </a>
      </h3>
      <AnalyticsBreakdown
        label={service.name}
        rows={[
          {
            id: 'cpu',
            label: 'CPU',
            value: formatPercent(service.cpuPercent) ?? 'No data',
            ...share(service.cpuPercent),
          },
          {
            id: 'memory',
            label: 'Memory',
            value: formatPercent(service.memoryPercent) ?? 'No data',
            ...share(service.memoryPercent),
          },
          {
            id: 'disk',
            label: 'Disk',
            value: formatPercent(service.diskPercent) ?? 'No data',
            ...share(service.diskPercent),
          },
          {
            id: 'connections',
            label: service.kind === 'valkey' ? 'Connected clients' : 'Active connections',
            value: service.connections === null ? 'No data' : String(service.connections),
          },
        ]}
      />
    </div>
  );
}

function NotConnected({ health }: { health: AdminHealth }) {
  const description =
    health.notConnectedReason === 'no_project'
      ? `No DigitalOcean project is configured for ${ENVIRONMENT_NAMES[health.environment]} here, so there is nothing to read. Nothing is estimated in its place.`
      : 'platform-api has no read-only DigitalOcean token in this environment, so there is nothing to read. Nothing is estimated in its place.';
  return (
    <EmptyPageState
      icon={<HealthGlyph />}
      title="Health isn't connected to DigitalOcean in this environment"
      description={description}
    />
  );
}

interface Selection {
  range: HealthRange;
}

/** The query string of a choice: production and 24 hours are the defaults and stay out of the address. */
function searchOf(selection: Selection): string {
  const search = new URLSearchParams();
  if (selection.range !== '24h') search.set('range', selection.range);
  const text = search.toString();
  return text ? `?${text}` : '';
}

/**
 * Health (/): every Trace app and the services behind it, as DigitalOcean reports them, for this Trace Admin's own
 * environment (platform-api answers for the environment it runs in; the page names it) and a period, in the address
 * (?range=7d). Every staff role reads it; platform-api reads
 * DigitalOcean (cached about 30 seconds) and audits each read. Nothing on the page is estimated: a value DigitalOcean
 * did not return says "No data", and a read that failed is listed.
 */
export function HealthPage() {
  const { api } = useAdmin();
  const [params, setParams] = useSearchParams();
  const fromAddress = useMemo(() => ({ range: rangeParam(params.get('range')) }), [params]);
  // The choice lives in state and the address follows it (replace), so the page answers a click at once. The router
  // commits a navigation later than the click (a transition), and a choice built from the address alone could undo
  // the one made just before it. A change of the address from elsewhere (a link, back) is adopted.
  const [selection, setSelection] = useState(fromAddress);
  // The addresses this page wrote that the router has not committed yet, oldest first: each is its own echo, never
  // a change from elsewhere.
  const pending = useRef<string[]>([]);
  useEffect(() => {
    const search = searchOf(fromAddress);
    const echo = pending.current.indexOf(search);
    if (echo >= 0) {
      pending.current = pending.current.slice(echo + 1);
      return;
    }
    pending.current = [];
    setSelection((current) => (searchOf(current) === search ? current : fromAddress));
  }, [fromAddress]);
  useEffect(() => {
    const search = searchOf(selection);
    const committed =
      pending.current.length > 0 ? pending.current[pending.current.length - 1] : searchOf(fromAddress);
    if (search === committed) return;
    pending.current = [...pending.current, search];
    setParams(search, { replace: true });
    // Only the selection drives this; fromAddress is read for the comparison, not followed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, setParams]);
  const { range } = selection;
  const now = useNow(1000);
  // The uptime grid's period (30 or 90 days, 90 by default); the strip's Uptime figure follows it.
  const [uptimePeriod, setUptimePeriod] = useState<UptimePeriod>(90);
  const read = useHealthRead(range, {
    read: (period, refresh, signal) => api.readHealth(period, refresh, signal),
  });
  const health = read.failure ? null : read.data;
  // Keys expired or due within their reminder windows (the navigation badge's count): one line linking to Keys.
  const keysDue = keysNeedingAttention(useKeysSummary().summary);
  const choose = (next: Partial<Selection>) => setSelection((current) => ({ ...current, ...next }));

  let body: ReactNode;
  if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="health" onRetry={read.refresh} />;
  } else if (!health) {
    body = <LoadingBlock label="Reading DigitalOcean" />;
  } else if (!health.connected) {
    body = <NotConnected health={health} />;
  } else {
    const attention = appsNeedingAttention(health);
    // After a change of period the previous period's answer stays only as a dimmed, busy placeholder, named as such,
    // until the new one arrives (no skeleton flash); a failed read of the new period replaces it with the problem.
    body = (
      <div
        className="admin-stack admin-health"
        data-switching={read.switching || undefined}
        aria-busy={read.switching || undefined}
      >
        {read.switching ? (
          <p className="admin-health-switching" role="status">
            Loading {RANGE_NAMES[range].toLowerCase()}…
          </p>
        ) : null}
        {health.source === 'fake' ? (
          <div className="admin-dev-note" role="note">
            <p>
              Synthetic data: this environment reads Trace&apos;s local stand-in for DigitalOcean, not
              DigitalOcean itself.
            </p>
          </div>
        ) : null}
        <UptimeGrid
          history={health.uptimeHistory}
          apps={health.apps}
          period={uptimePeriod}
          onPeriod={setUptimePeriod}
        />
        <HealthMetrics health={health} uptimePeriod={uptimePeriod} />
        {attention.length > 0 ? <AttentionBanner apps={attention} /> : null}
        {health.failures.length > 0 ? <PartialNotice health={health} /> : null}
        <PeriodChoice range={range} onRange={(next) => choose({ range: next })} />
        <HealthCharts health={health} />
        <PageSection title="Apps">
          {health.apps.length > 0 ? (
            <AppsTable apps={sortApps(health.apps)} now={now} />
          ) : (
            <p className="admin-muted">DigitalOcean lists no app in this project.</p>
          )}
        </PageSection>
        <div className="admin-health-split">
          <PageSection
            title="Shared services"
            description="Managed databases and queues, their latest values."
          >
            {health.services.length > 0 ? (
              health.services.map((service) => <ServiceRows key={service.id} service={service} />)
            ) : (
              <p className="admin-muted">DigitalOcean lists no managed database in this project.</p>
            )}
          </PageSection>
          <PageSection title="Activity" description="Deploys and incidents of the period, newest first.">
            <Activity key={health.range} health={health} now={now} />
          </PageSection>
        </div>
      </div>
    );
  }

  return (
    <AdminPage title="Health">
      <PageHeading
        title="Health"
        actions={
          <div className="admin-health-actions">
            {/* Which environment this is, in plain words: each Trace Admin shows its own (admin.dev: Dev). */}
            {health ? (
              <p className="admin-health-environment">
                <span className="admin-sr-only">Environment: </span>
                {ENVIRONMENT_NAMES[health.environment]}
              </p>
            ) : null}
            {health?.connected ? (
              <CheckedAt health={health} now={now} refreshFailure={read.refreshFailure} />
            ) : null}
            <button
              type="button"
              className="admin-action"
              onClick={read.refresh}
              aria-disabled={read.loading || undefined}
            >
              {/* A steady label: the page refreshes itself every 30 seconds without visible change. */}
              Check again
            </button>
            {health?.consoleUrl ? (
              <a className="admin-action" href={health.consoleUrl} target="_blank" rel="noopener noreferrer">
                Open DigitalOcean
              </a>
            ) : null}
          </div>
        }
      />
      {keysDue > 0 ? (
        <p className="admin-keys-health-line">
          <KeyGlyph size={16} />
          <span>{keysAttentionWords(keysDue)}</span> <span aria-hidden="true">→</span>{' '}
          <Link to="/keys">Keys</Link>
        </p>
      ) : null}
      {body}
    </AdminPage>
  );
}
